import { React, useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "../style";
import { LogoName, LogoIcon, img4 } from "../assets";
import { footerLinks, socialMedia, creator } from "../constants/index";
import { PiMapPinFill } from "react-icons/pi"
import { MdCall, MdEmail } from "react-icons/md";
import { motion, useMotionTemplate, useMotionValue, animate } from "framer-motion";


const Footer = () => {
  const [active, setActive] = useState("Home");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  const backgroundImage = useMotionTemplate`radial-gradient(100% 100% at 90% 100%, #040F49 80%,)`;
  
  useEffect(() => {
      // Check authentication status on component mount
      const checkAuth = () => {
        const authStatus = localStorage.getItem('isAuthenticated') === 'true';
        setIsAuthenticated(authStatus);
      };
      
      checkAuth();
      
      // Listen for storage changes (in case auth changes in another tab)
      window.addEventListener('storage', checkAuth);
      
      return () => {
        window.removeEventListener('storage', checkAuth);
      };
    }, []);
  
    const handleLogout = () => {
      // Clear user session
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('user');
      localStorage.removeItem('isAdmin');
      localStorage.removeItem('loginTime');
      
      setIsAuthenticated(false);
      
      // Redirect to home page
      navigate('/');
    };
  

  return (
  <motion.div 
    style={{ 
      backgroundImage: `url(${img4})`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      zIndex: 0,
    }}
    className={`bg-primary w-full flex flex-col pt-20`}
  >
    <div className={`${styles.flexCenter} flex-col w-full mb-10`}>
      <div className={`${styles.flexCenter} flex flex-col gap-2`}>
        <Link to="/home" className={`${styles.flexCenter} flex flex-col gap-2 mt-4`}>
          <img
            src={LogoIcon}
            alt="HSL"
            className="w-[150px] h-[150px] object-contain"
          />
          <img
            src={LogoName}
            alt="HSL"
            className="w-[120px] h-[80px] object-contain"
          />
        </Link>
        <p className={`kerning-normal ss:text-sm text-ss text-white font-medium text-center max-w-[400px]`}>
          Backing the Builders of Africa's Creative and Digital Future
        </p>
      </div>

      <div className="flex flex-col md:flex-row justify-center items-start md:gap-4 gap-8 mt-20"> 
        <div className=" flex flex-row gap-6">
          {footerLinks.map((footerlink) => (
            <div key={footerlink.title} className={`flex flex-col ss:my-0 my-4 min-w-[150px]`}>
              <p className="kerning-normal font-semibold ss:text-sm text-ss text-second leading-[27px] text-second">
                {footerlink.title}
              </p>
              <ul className="list-none mt-2">
                {footerlink.links.map((link, index) => (
                  <li
                    key={link.name}
                    className={`ss:text-sm text-ss leading-[24px] overflow-hidden 
                      ${index !== footerlink.links.length - 1 ? "mb-2" : "mb-0"}
                      ${active === footerlink.title ? "text-tertiary font-bold " : "font-medium text-tertiary"} 
                    `} 
                      onClick={() => setActive(link.name)}                  
                  >
                    <a href={`${link.href}`} className="kerning-normal hover:text-second font-medium text-tertiary">{link.name}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex flex-row gap-6">
          <div className="flex flex-col gap-2">
            <p className="kerning-normal ss:text-sm text-ss font-semibold text-second">Connect</p> 
            <div className="flex flex-row gap-2 items-center">
              <MdCall size={18} color={'white'} />
              <p className="kerning-normal ss:text-sm text-ss text-tertiary leading-[27px]">+254 793 872 627</p>
            </div>
            <div className="flex flex-row gap-2 items-center">
              <MdEmail size={18} color={'white'} />
              <div className="flex flex-col">
                <p className="kerning-normal ss:text-sm text-ss text-tertiary leading-[27px]">info@afrinextventures.com</p>
              </div>
            </div> 
            <div className="flex flex-row gap-2 items-center">
              <PiMapPinFill size={18} color={'white'} />
              <p className="kerning-normal ss:text-sm text-ss text-tertiary leading-[27px]">Westlands, Nairobi Kenya</p>
            </div>
            <div className="flex flex-row gap-2 items-center">
                  {isAuthenticated ? (
                    <button 
                      onClick={() => {
                        // Clear user session
                        localStorage.removeItem('isAuthenticated');
                        localStorage.removeItem('user');
                        localStorage.removeItem('isAdmin');
                        localStorage.removeItem('loginTime');
                        // Redirect to home page or login page
                        window.location.href = '/';
                      }}
                      className="kerning-normal hover:text-second text-tertiary ss:text-sm text-ss transition-colors duration-300"
                    >
                      Log Out
                    </button>
                  ) : (
                    <Link 
                      to="/login" 
                      className="kerning-normal hover:text-second text-tertiary ss:text-sm text-ss transition-colors duration-300"
                    >
                      Log In
                    </Link>
                  )}
                </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className={`flex flex-col gap-2`}>
              <h4 className="kerning-normal font-semibold ss:text-sm text-ss leading-[27px] text-second">
                Follow Us
              </h4>
              {socialMedia.map((social, index) => (
                <div className="flex flex-row gap-2 items-center">
                  <img
                    key={social.id}
                    src={social.icon}
                    alt={social.id}
                    className={`w-[18px] h-[18px] object-contain
                      ${index !== socialMedia.length - 1 ? "mr-4" : "mr-4"}
                    `} 
                  />
                  <motion.div 
                    key={social.id}
                    initial="initial"
                    whileHover="hovered" 
                    className={`relative font-medium ss:text-sm text-ss cursor-pointer overflow-hidden
                      ${active === social.title ? "text-second" : "text-tertiary"}
                    `}
                  >
                    <motion.div 
                      variants={{
                          initial: { y: 0 },
                          hovered: { y: "-100%" },
                        }}
                      onClick={() => window.open(social.link)}
                    >
                      {social.title}
                    </motion.div>
                    <motion.div 
                      variants={{
                        initial: { y: "100%" },
                        hovered: { y: 0 },
                      }}
                      onClick={() => window.open(social.link)}
                      className="absolute inset-0"
                    >
                      {social.title}
                    </motion.div>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="w-full flex flex-col gap-4 items-center justify-center p-6 border-t-2 border-t-secondary">
      <p className="kerning-normal text-center text-white font-medium text-ss leading-[27px]">
        Copyright Ⓒ 2026 Afrinext VC Limited. All Rights Reserved.
      </p> 
      {creator.map((creator, index) => (
          <div
            key={index}
            className={`font-medium cursor-pointer text-sm overflow-hidden`}
            onClick={() => window.open(creator.link)}
          >
            <p className="kerning-normal text-ss text-white">Site built by <a className="kerning-normal text-white underline">{creator.title}</a></p>
          </div>
        ))} 
    </div>
  </motion.div>
  )
}
export default Footer;